import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import {
  InsuranceDTO,
  InsuranceType,
  OwnerOptionDTO,
  OwnerTypeDTO,
} from '../../../api/useInsuranceQuery';
import { FormFields } from '../../../components/UpsertInsuranceModal/UpsertInsuranceModal.meta';

interface Props {
  label?: string;
  insuranceType: InsuranceType;
  data: InsuranceDTO;
  setAlert?: (message: string | null) => void;
}

export const AccountOwnerSelect = ({
  label = 'Account Owner',
  insuranceType,
  data,
  setAlert,
}: Props) => {
  const formBag = useFormContext<FormFields>();

  const beneficiaries = formBag.watch('AccountBeneficiaries.Beneficiaries');

  const watchedOwnerModel = useWatch({
    name: 'OwnerModel',
    control: formBag.control,
  });

  const groupOptions = Object.keys(data.Options.InsuranceOwnerOptions)
    .filter(key => {
      if (key === 'Trusts') {
        return insuranceType === InsuranceType.Life;
      }
      if (key === 'Businesses') {
        return (
          insuranceType === InsuranceType.Life ||
          insuranceType === InsuranceType.PropertyAndCasualty
        );
      }

      return true;
    })
    .map(key => ({
      label: key,
      options: (data.Options.InsuranceOwnerOptions[key] || []).map(owner => ({
        label: owner.Name,
        value: owner.Id,
      })) as { label: string; value: string }[],
    }));

  return (
    <Controller
      control={formBag.control}
      name="AccountOwner"
      render={({ field: { value, onChange } }) => {
        return (
          <Select
            label={label}
            value={groupOptions
              .map(({ options }) => options)
              .flat()
              .find(option => option.label === value)}
            options={groupOptions}
            onChange={opt => {
              onChange(opt?.label);
              if (opt) {
                const selectedOwnerOption: OwnerOptionDTO = Object.values(
                  data.Options.InsuranceOwnerOptions,
                )
                  .map(items => items)
                  .flat()
                  .find(option => option.Name === opt.label);
                formBag.setValue('OwnerModel', [
                  {
                    ...watchedOwnerModel[0],
                    Id: opt.value,
                    Name: opt.label,
                    OwnerType: selectedOwnerOption.OwnerType,
                    Percentage: 1,
                  },
                ]);
                if (setAlert) {
                  if (
                    beneficiaries.length > 0 ||
                    selectedOwnerOption.OwnerType === OwnerTypeDTO.Trust
                  ) {
                    setAlert(
                      "Changing account ownership of this account will result in the trust's beneficiary designations superseding the existing account beneficiaries.",
                    );

                    formBag.setValue('AccountBeneficiaries.Beneficiaries', []);
                  } else {
                    setAlert(null);
                  }
                }
              }
            }}
            isPortal
          />
        );
      }}
    />
  );
};
